import {
  CurrencyValue,
  type DropdownOptionAPI,
  FieldValue,
  HookedRecordResult,
  type ITdmModifier,
  type Row,
} from '@ingestro/importer-react'

import type {
  EditionData,
  Metadata,
} from 'modules/universal_importer/components/NuvoImporter/types'
import type { Column } from 'modules/universal_importer/components/NuvoImporter/useColumnsMapping'
import i18n from 'modules/core/lib/i18n'

// Type alias to avoid linter issues with any
export type CompatibleColumns = unknown[]

// Type guards and constructors to avoid type assertions
export function isHookedRecordResult(value: unknown): value is HookedRecordResult {
  return typeof value === 'object' && value !== null && 'value' in value && 'info' in value
}

export function isFieldValue(value: unknown): value is FieldValue {
  return (
    typeof value === 'string' ||
    typeof value === 'number' ||
    typeof value === 'boolean' ||
    value === null ||
    value === undefined ||
    Array.isArray(value)
  )
}

function isCurrencyValue(value: unknown): value is CurrencyValue {
  return typeof value === 'object' && value !== null && 'value' in value && 'type' in value
}

// Helper to extract field value from row
export function getFieldValue(
  row: Record<string, FieldValue | CurrencyValue | HookedRecordResult>,
  key: string
): FieldValue {
  if (isHookedRecordResult(row[key]) || isCurrencyValue(row[key])) {
    return row[key]?.value || ''
  } else if (isFieldValue(row[key])) {
    return row[key]
  }
  return null
}

// Helper to create HookedRecordResult for errors
export function createHookedRecordResult(
  fieldValue: FieldValue,
  message: string
): HookedRecordResult {
  return {
    value: fieldValue,
    info: [{ message, level: 'error' }],
  }
}

// Helper to create RowResult from validation/transformation results
function createRowResult(
  value: FieldValue | CurrencyValue | HookedRecordResult
): HookedRecordResult | FieldValue {
  if (isHookedRecordResult(value)) {
    return value // Return HookedRecordResult for errors
  } else if (isFieldValue(value)) {
    return value // Return FieldValue for success
  } else {
    return value.value // Extract value from CurrencyValue
  }
}

// Type for customization - minimal interface to avoid circular dependencies
type ColumnCustomizationInput = {
  key: string
  hidden?: boolean
  customizations?: Array<{
    type: string
    computeWith: string
    parameters?: Array<{ key: string; value: string }> | null
  }>
}

// Transformation function type matching transformations.ts
type TransformationFunction = (
  row: Record<string, FieldValue | CurrencyValue | HookedRecordResult>,
  metadata: Metadata,
  columns: CompatibleColumns,
  currentColumnKey: string,
  parameters?: { key: string; value: string }[] | null
) => { value: string }

// Validation function type matching validations.ts
type ValidationFunction = (
  row: Record<string, FieldValue | CurrencyValue | HookedRecordResult>,
  metadata: Metadata,
  columns: CompatibleColumns,
  currentColumnKey: string,
  parameters?: { key: string; value: string }[] | null,
  changeLog?: Row | null
) => { value: FieldValue | CurrencyValue | HookedRecordResult }

// Helper to apply transformations and validations to a row
export function applyCustomizationsToRow(
  row: Record<string, FieldValue | CurrencyValue | HookedRecordResult>,
  customizations: ColumnCustomizationInput[],
  transformationFunctions: Record<string, TransformationFunction>,
  validationFunctions: Record<string, ValidationFunction>,
  metadata: Metadata,
  columns: CompatibleColumns,
  options?: {
    wrapWithRowResult?: boolean
    changeLog?: Row | null
  }
): Record<string, FieldValue | CurrencyValue | HookedRecordResult> {
  const newRow: Record<string, FieldValue | CurrencyValue | HookedRecordResult> = { ...row }

  customizations.forEach((column: ColumnCustomizationInput) => {
    if (column.customizations) {
      column.customizations.forEach((customization) => {
        // Apply transformation function
        const transformationFunction = transformationFunctions[customization.computeWith]
        if (transformationFunction && Object.prototype.hasOwnProperty.call(newRow, column.key)) {
          const result = transformationFunction(
            newRow,
            metadata,
            columns,
            column.key,
            customization.parameters
          )
          newRow[column.key] = options?.wrapWithRowResult
            ? createRowResult(result.value)
            : result.value
        }

        // Apply validation function
        const validationFunction = validationFunctions[customization.computeWith]
        if (validationFunction && Object.prototype.hasOwnProperty.call(newRow, column.key)) {
          const result = validationFunction(
            newRow,
            metadata,
            columns,
            column.key,
            customization.parameters,
            options?.changeLog
          )
          newRow[column.key] = options?.wrapWithRowResult
            ? createRowResult(result.value)
            : result.value
        }
      })
    }
  })

  return newRow
}

// Type guard to check if object has key property
export function hasKey(obj: unknown): obj is { key: string; [key: string]: unknown } {
  return typeof obj === 'object' && obj !== null && 'key' in obj
}

// Helper to find column by key
export function findColumnByKey(columns: CompatibleColumns, targetKey: string) {
  if (Array.isArray(columns)) {
    return columns.find((col) => hasKey(col) && col.key === targetKey) || null
  }
  return null
}

/**
 * Generates mappingValidation logic string for a column based on its validations
 * Following Nuvo's mappingValidation format where the logic represents the VALID case
 * @param columnKey - The key of the column to generate validation for
 * @param validations - Array of validation objects for this column
 * @param allColumns - All columns to check for cross-column dependencies
 * @returns Object with logic and errorMessage, or undefined if no mapping validation needed
 */
export function generateMappingValidation(
  columnKey: string,
  validations?: Array<{ validate: string; columns?: string[] }>,
  allColumns?: CompatibleColumns
): { logic: string; errorMessage: string } | undefined {
  if (!validations || validations.length === 0) {
    return undefined
  }

  const getColumnLabel = (key: string) => {
    const col = allColumns ? findColumnByKey(allColumns, key) : null
    if (col && typeof col === 'object' && 'label' in col && typeof col.label === 'string') {
      return col.label
    }
    return key
  }

  const hasRequired = validations.some((v) => v.validate === 'required')
  const requiredWith = validations.find((v) => v.validate === 'required_with')
  const requiredWithAll = validations.find((v) => v.validate === 'required_with_all')

  // If column has 'required' validation, it must always be mapped
  if (hasRequired) {
    const columnLabel = getColumnLabel(columnKey)
    return {
      logic: `mapped('${columnKey}')`,
      errorMessage: i18n.t('universal_importer.mapping_validation.required', { columnLabel }),
    }
  }

  // Handle required_with: this column is required if ANY of the specified columns are mapped
  // Logic: column must be mapped OR none of the dependent columns are mapped
  if (requiredWith && requiredWith.columns && requiredWith.columns.length > 0) {
    // Filter out dependent columns that don't exist in the current schema
    // (e.g. identifier_type is excluded for certain countries).
    // Only filter when allColumns is provided; otherwise keep original list.
    const dependentColumns = allColumns
      ? requiredWith.columns.filter((col) => findColumnByKey(allColumns, col) !== null)
      : requiredWith.columns
    if (dependentColumns.length > 0) {
      const andConditions = dependentColumns.map((col) => `!mapped('${col}')`).join(' && ')
      const columnLabel = getColumnLabel(columnKey)
      const dependentColumnLabels = dependentColumns.map(getColumnLabel)
      const dependentColumnLabelsText = dependentColumnLabels.join(', ')
      return {
        logic: `mapped('${columnKey}') || (${andConditions})`,
        errorMessage: i18n.t('universal_importer.mapping_validation.required_with', {
          columnLabel,
          dependentColumnLabels: dependentColumnLabelsText,
        }),
      }
    }
  }

  // Handle required_with_all: this column is required if ALL of the specified columns are mapped
  // Logic: column must be mapped OR at least one of the dependent columns is not mapped
  if (requiredWithAll && requiredWithAll.columns && requiredWithAll.columns.length > 0) {
    // Filter out dependent columns that don't exist in the current schema.
    // Only filter when allColumns is provided; otherwise keep original list.
    const dependentColumns = allColumns
      ? requiredWithAll.columns.filter((col) => findColumnByKey(allColumns, col) !== null)
      : requiredWithAll.columns
    if (dependentColumns.length === 0) return undefined
    const orConditions = dependentColumns.map((col) => `!mapped('${col}')`).join(' || ')
    const columnLabel = getColumnLabel(columnKey)
    const dependentColumnLabels = dependentColumns.map(getColumnLabel)
    const dependentColumnLabelsText = dependentColumnLabels.join(', ')
    return {
      logic: `mapped('${columnKey}') || (${orConditions})`,
      errorMessage: i18n.t('universal_importer.mapping_validation.required_with_all', {
        columnLabel,
        dependentColumnLabels: dependentColumnLabelsText,
      }),
    }
  }

  return undefined
}

/**
 * Adds mappingValidation property to columns based on their validations
 * @param columns - Array of column definitions
 * @returns Columns array with mappingValidation added where needed
 */
export function addMappingValidations(columns: CompatibleColumns): CompatibleColumns {
  const hasValidations = (
    col: unknown
  ): col is {
    key: string
    validations?: Array<{ validate: string; columns?: string[] }>
    mappingValidation?: { logic: string; errorMessage: string }
  } => {
    return (
      typeof col === 'object' &&
      col !== null &&
      'key' in col &&
      typeof col.key === 'string' &&
      (!('validations' in col) || Array.isArray(col.validations))
    )
  }

  const typedColumns = Array.isArray(columns) ? columns.filter(hasValidations) : []

  return typedColumns.map((col) => {
    const mappingValidation = generateMappingValidation(col.key, col.validations, columns)

    // Only add mappingValidation if it doesn't already exist and we generated one
    if (mappingValidation && !col.mappingValidation) {
      return {
        ...col,
        mappingValidation,
      }
    }

    return col
  })
}

// --- useFileUploadInEdition helpers ---

// Separator used to build composite keys from multiple identifier columns.
// Chosen because the pipe character is unlikely to appear in emails or dates.
export const COMPOSITE_KEY_SEPARATOR = '|'

/**
 * Builds a composite key from a row by concatenating the values of all
 * identifier columns, separated by COMPOSITE_KEY_SEPARATOR.
 * Returns an empty string if any identifier value is missing.
 */
export function buildCompositeKey(
  row: Record<string, unknown>,
  identifierColumnKeys: string[]
): string {
  const parts = identifierColumnKeys.map((key) => {
    const cell = row[key]
    // Defensively unwrap HookedRecordResult/CurrencyValue objects to their primitive value.
    // Identifier columns are disabled in the updater flow so they should always be plain strings,
    // but Nuvo could theoretically wrap them after validation/transformation passes.
    const raw = typeof cell === 'object' && cell !== null && 'value' in cell ? cell.value : cell
    return String(raw ?? '').trim()
  })
  if (parts.some((p) => p === '')) return ''
  return parts.join(COMPOSITE_KEY_SEPARATOR)
}

type MissingColumnsResult = {
  missingColumnKeys: string[]
  missingColumnType: Map<string, string>
  editionDataByIdentifier: Map<string, EditionData[number]>
}

export function toNuvoDropdownOptions(
  dropdownOptions: Column['dropdownOptions']
): DropdownOptionAPI[] | undefined {
  return dropdownOptions?.map((opt) => {
    const base = {
      label: opt.label,
      alternativeMatches: opt.alternativeMatches,
      description: opt.description ?? undefined,
      validations: opt.validations ?? undefined,
    }
    return opt.type === 'int' || opt.type === 'float'
      ? { ...base, type: opt.type, value: Number(opt.value) }
      : { ...base, type: 'string' as const, value: opt.value }
  })
}

export function addMissingRequiredColumns(
  tdm: ITdmModifier,
  rawColumns: Column[],
  requiredColumnKeys: Set<string>,
  mappedKeys: Set<string>,
  fileEditionData: EditionData,
  identifierColumnKeys: string[]
): MissingColumnsResult {
  const missingColumnKeys: string[] = []
  const missingColumnType = new Map<string, string>()
  const editionDataByIdentifier = new Map<string, EditionData[number]>()

  rawColumns
    .filter((col) => requiredColumnKeys.has(col.key) && !mappedKeys.has(col.key))
    .forEach((col) => {
      const {
        alternativeMatches: _,
        columnSize: _cs,
        allowCustomOptions: _aco,
        dropdownOptions: _do,
        ...rest
      } = col
      tdm.addColumn({
        ...rest,
        description: rest.description ?? undefined,
        dropdownOptions: toNuvoDropdownOptions(col.dropdownOptions),
      })
      missingColumnKeys.push(col.key)
      const valueType = col.dropdownOptions?.[0]?.type ?? col.columnType
      if (valueType) {
        missingColumnType.set(col.key, valueType)
      }
    })

  fileEditionData.forEach((row) => {
    const compositeKey = buildCompositeKey(row, identifierColumnKeys)
    if (compositeKey !== '') editionDataByIdentifier.set(compositeKey, row)
  })

  return { missingColumnKeys, missingColumnType, editionDataByIdentifier }
}

export function convertEditionValue(
  val: string | string[] | unknown,
  colType: string | undefined
): FieldValue {
  if (val === null || val === undefined) return null
  if (Array.isArray(val)) return val
  if (typeof val === 'boolean') return val
  if (typeof val === 'number') return val
  if (typeof val !== 'string') return null
  if (val.trim() === '') return null
  if (colType === 'int' || colType === 'float') {
    const num = Number(val)
    return Number.isNaN(num) ? null : num
  }
  if (colType === 'boolean') return val === 'true'
  return val
}

export type DropdownMaps = {
  labelToValue: Map<string, Map<string, string>>
  valueToLabel: Map<string, Map<string, string>>
}

export function buildDropdownMaps(columns: Column[]): DropdownMaps {
  const labelToValue = new Map<string, Map<string, string>>()
  const valueToLabel = new Map<string, Map<string, string>>()
  columns.forEach((col) => {
    if (!col.dropdownOptions || col.dropdownOptions.length === 0) return
    const ltvMap = new Map<string, string>()
    const vtlMap = new Map<string, string>()
    col.dropdownOptions.forEach((opt) => {
      ltvMap.set(opt.label.toLowerCase(), opt.value)
      vtlMap.set(opt.value.toLowerCase(), opt.label)
      opt.alternativeMatches?.forEach((alt) => ltvMap.set(alt.toLowerCase(), opt.value))
    })
    labelToValue.set(col.key, ltvMap)
    valueToLabel.set(col.key, vtlMap)
  })
  return { labelToValue, valueToLabel }
}

export function annotateChangedColumns(
  row: Record<string, FieldValue | HookedRecordResult>,
  editionRow: EditionData[number],
  identifierColumnKeys: string[],
  skipKeys: Set<string>,
  message: string,
  dropdownMaps: DropdownMaps,
  multiSelectKeys: Set<string>
): Record<string, FieldValue | HookedRecordResult> {
  const annotatedRow: Record<string, FieldValue | HookedRecordResult> = {}
  const { labelToValue, valueToLabel } = dropdownMaps
  const identifierKeySet = new Set(identifierColumnKeys)

  Object.keys(row).forEach((key) => {
    const cellValue = row[key]

    if (identifierKeySet.has(key) || skipKeys.has(key)) {
      annotatedRow[key] = cellValue
      return
    }

    annotatedRow[key] = computeCellAnnotation(
      cellValue,
      editionRow[key],
      key,
      message,
      labelToValue,
      valueToLabel,
      multiSelectKeys
    )
  })

  return annotatedRow
}

/**
 * Computes the annotation for a single cell by comparing its current value
 * with the original DB value. Returns the cell value with the appropriate
 * info/warning annotation, or unchanged if values match.
 */
export function computeCellAnnotation(
  cellValue: FieldValue | HookedRecordResult,
  editionVal: string | string[] | HookedRecordResult | undefined,
  key: string,
  message: string,
  labelToValue: Map<string, Map<string, string>>,
  valueToLabel: Map<string, Map<string, string>>,
  multiSelectKeys: Set<string>
): FieldValue | HookedRecordResult {
  const hooked = isHookedRecordResult(cellValue)
  const rawValue = hooked ? (cellValue.value ?? null) : cellValue
  const existingInfos = hooked ? (cellValue.info ?? []) : []
  const editionHasValue = editionVal != null && editionVal !== ''
  const fileHasValue = rawValue != null && String(rawValue) !== ''

  if (!editionHasValue && !fileHasValue) {
    return cellValue
  }

  const oldStr = String(editionVal ?? '')
  const fileStr = String(rawValue ?? '')
  const ltvMap = labelToValue.get(key)
  const vtlMap = valueToLabel.get(key)
  const isMulti = multiSelectKeys.has(key)

  const resolveValue = (val: string, map: Map<string, string> | undefined): string => {
    if (!isMulti) return map?.get(val.toLowerCase()) ?? val
    return val
      .split(',')
      .map((v) => map?.get(v.trim().toLowerCase()) ?? v.trim())
      .join(', ')
  }

  // For multiselect: normalize both sides to sorted arrays of IDs
  // so order doesn't matter and format differences (spaces, labels vs values) are handled.
  // oldStr comes from the DB (always IDs), fileStr may contain labels or IDs from the user.
  if (isMulti) {
    const normalizeToIds = (val: string): string[] =>
      val
        .split(',')
        .map((v) => v.trim().toLowerCase())
        .filter((v) => v !== '')
        .sort()

    const resolveToIds = (val: string, map: Map<string, string> | undefined): string[] =>
      val
        .split(',')
        .map((v) => (map?.get(v.trim().toLowerCase()) ?? v.trim()).toLowerCase())
        .filter((v) => v !== '')
        .sort()

    const resolvedOld = normalizeToIds(oldStr)
    const resolvedFile = resolveToIds(fileStr, ltvMap)

    if (
      resolvedOld.length === resolvedFile.length &&
      resolvedOld.every((v, i) => v === resolvedFile[i])
    ) {
      return cellValue
    }

    const displayOldStr = editionHasValue ? resolveValue(oldStr, vtlMap) : ''
    const isClearing = editionHasValue && !fileHasValue
    const level = isClearing ? ('warning' as const) : ('info' as const)

    return {
      value: rawValue,
      info: [...existingInfos, { message: `${message}: ${displayOldStr}`, level }],
    }
  }

  const resolvedFileStr = ltvMap ? resolveValue(fileStr, ltvMap) : fileStr

  if (oldStr.toLowerCase() === resolvedFileStr.toLowerCase()) {
    return cellValue
  }

  const displayOldStr = editionHasValue ? resolveValue(oldStr, vtlMap) : ''
  const isClearing = editionHasValue && !fileHasValue
  const level = isClearing ? ('warning' as const) : ('info' as const)

  return {
    value: rawValue,
    info: [...existingInfos, { message: `${message}: ${displayOldStr}`, level }],
  }
}
